<template>
  <div class="oai-ResearchReviewStep">
    <OnboardingAiStepHeader
      :subtitle="$t('onboarding.ai.research_review.subtitle')"
      :title-key-accent="$t('onboarding.ai.research_review.title_accent')"
      :wizard-step="wizardStep"
      class="oai-ResearchReviewStep_Header"
      title-key-name="outside"
      title-key-path="onboarding.ai.research_review.title_prefix"
    />

    <div class="oai-ResearchReviewStep_Grid">
      <section class="oai-ResearchReviewStep_Panel">
        <div class="oai-ResearchReviewStep_PanelInner">
          <AppTitle :level="4" class="oai-ResearchReviewStep_PanelTitle">
            {{ $t('onboarding.ai.research_review.research_title') }}
          </AppTitle>

          <div class="oai-ResearchReviewStep_ResearchTableWrap">
            <OnboardingAiBadge class="oai-ResearchReviewStep_ResearchBadge">
              {{ $t('onboarding.ai.research_review.badge') }}
            </OnboardingAiBadge>

            <OnboardingAiResearchTable
              :rows="researchTableRows"
              class="oai-ResearchReviewStep_ResearchTable"
              scrollable
            />
          </div>
        </div>
      </section>

      <section class="oai-ResearchReviewStep_PanelRight oboard-truncated-text">
        <div class="oai-ResearchReviewStep_PanelRightInner">
          <AppTitle :level="4" class="oai-ResearchReviewStep_PanelTitle">
            {{ $t('onboarding.ai.research_review.setup_title') }}
          </AppTitle>

          <div class="oai-ResearchReviewStep_SetupTags">
            <span v-for="tag in setupTags" :key="tag" class="oai-ResearchReviewStep_SetupTag">
              {{ tag }}
            </span>
            <span class="oai-ResearchReviewStep_StructurePath">
              <span
                v-for="(layer, index) in structurePath"
                :key="layer"
                class="oai-ResearchReviewStep_StructurePathItem"
              >
                <span class="oboard-truncated-text">{{ layer }}</span>
                <AppIcon
                  v-if="index < structurePath.length - 1"
                  class="oai-ResearchReviewStep_StructureArrow"
                  height="16"
                  icon-name="arrow-right-simple-next"
                  width="16"
                />
              </span>
            </span>
          </div>
        </div>

        <div class="oai-ResearchReviewStep_NotesFields">
          <div
            v-for="field in NOTE_FIELD_CONFIG"
            :key="field.key"
            class="oai-ResearchReviewStep_NoteField"
          >
            <label class="oai-ResearchReviewStep_NotesLabel">
              {{ $t(field.labelKey) }}
            </label>

            <AppInput
              v-model="noteFields[field.key]"
              :placeholder="$t(field.placeholderKey)"
              class="oai-ResearchReviewStep_NoteInput"
              size="lg"
            />
          </div>
        </div>
      </section>
    </div>

    <OnboardingAiStepActions @next="handleNext" />
  </div>
</template>

<script setup>
import { computed, watch, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  ONBOARDING_AI_GOAL_OPTIONS,
  ONBOARDING_AI_STRUCTURE_OPTIONS,
  useOnboardingAiTracking
} from '@/composables/onboarding-ai-step'
import { EVENT_NAMES } from '@/tracking/amplitude-helpers'
import { mapResearchDataToTableRows } from '@/utils/onboarding-ai/research-mapper'
import {
  combineNoteFields,
  createEmptyNoteFields,
  getPillarsFilledCount,
  NOTE_FIELD_CONFIG,
  parseCombinedNotes
} from '@/utils/onboarding-ai/research-notes'

import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppInput from '@/components/ui/AppInput/AppInput'
import AppTitle from '@/components/ui/AppTitle/AppTitle'
import OnboardingAiBadge from '@/components/ui/onboarding-ai/OnboardingAiBadge'
import OnboardingAiResearchTable from '@/components/ui/onboarding-ai/OnboardingAiResearchTable'
import OnboardingAiStepActions from '@/components/ui/onboarding-ai/OnboardingAiStepActions'
import OnboardingAiStepHeader from '@/components/ui/onboarding-ai/OnboardingAiStepHeader'

defineOptions({
  name: 'OnboardingAiResearchReviewStep'
})

const props = defineProps({
  wizardStep: {
    type: Number,
    required: true
  },

  modelValue: {
    type: String,
    default: ''
  },

  selectedGoals: {
    type: Array,
    default: () => []
  },

  selectedStructureId: {
    type: String,
    default: null
  },

  researchData: {
    type: Object,
    default: null
  }
})

const emit = defineEmits({
  'update:modelValue': null,
  next: null
})

const { logEvent } = useOnboardingAiTracking()

const { t } = useI18n()

const researchTableRows = computed(() => mapResearchDataToTableRows(props.researchData))

const setupTags = computed(() => {
  return props.selectedGoals.reduce((acc, goalId) => {
    const option = ONBOARDING_AI_GOAL_OPTIONS.find(({ id }) => id === goalId)
    if (option && option.labelKey) {
      acc.push(t(option.labelKey))
    }
    return acc
  }, [])
})

const structurePath = computed(() => {
  const option = ONBOARDING_AI_STRUCTURE_OPTIONS.find(({ id }) => id === props.selectedStructureId)
  return option?.layers ?? []
})

const noteFields = ref(createEmptyNoteFields())

watch(
  () => props.modelValue,
  value => {
    noteFields.value = parseCombinedNotes(value)
  },
  { immediate: true }
)

const handleNext = () => {
  emit('update:modelValue', combineNoteFields(noteFields.value))
  emit('next', {
    pillars_filled_count: getPillarsFilledCount(noteFields)
  })
}

onMounted(() => {
  logEvent(EVENT_NAMES.ONBOARDING_STRATEGIC_PILLARS_VIEWED)
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/onboarding-ai-step';

.oai-ResearchReviewStep {
  @extend %oai-wizard-step-shell;

  --step-max-width: 992px;
  --ob-oai-reserch-review-step-gap: 32px;
}

.oai-ResearchReviewStep_Header {
  max-width: calc((var(--step-max-width) - var(--ob-oai-reserch-review-step-gap)) / 2);
}

.oai-ResearchReviewStep_Grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--ob-oai-reserch-review-step-gap);
  width: 100%;
}

.oai-ResearchReviewStep_Panel {
  position: relative;
  grid-row: 1;
  grid-column: 1;
}

.oai-ResearchReviewStep_PanelInner {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  overflow: hidden;
}

.oai-ResearchReviewStep_PanelRight {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 36px;
  grid-row: 1;
  grid-column: 2;
}

.oai-ResearchReviewStep_PanelRightInner {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.oai-ResearchReviewStep_PanelTitle {
  margin: 0;
  font-weight: fw('semi-bold');
  color: $oai-text-primary;
}

.oai-ResearchReviewStep_ResearchTableWrap {
  position: relative;
  flex: 1;
  display: flex;
  min-height: 0;
  flex-direction: column;
}

.oai-ResearchReviewStep_ResearchTable {
  flex: 1;
  min-height: 0;
}

.oai-ResearchReviewStep_ResearchBadge {
  position: absolute;
  top: 0;
  right: 24px;
  transform: translateY(-50%);
  z-index: 1;
}

.oai-ResearchReviewStep_SetupTags {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.oai-ResearchReviewStep_SetupTag {
  padding: 2px 6px;
  border: 1px solid $grey-2-next;
  border-radius: 6px;
  background: $grey-3-next;
  font-size: $fs-14;
  line-height: 20px;
  color: $oai-text-tag;
}

.oai-ResearchReviewStep_StructurePath {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 2px 6px;
  min-height: 24px;
  border: 1px solid $grey-2-next;
  border-radius: 6px;
  background: $grey-3-next;
  font-size: $fs-14;
  line-height: 20px;
  color: $oai-text-tag;
}

.oai-ResearchReviewStep_StructureArrow {
  color: $grey-1-next;
}

.oai-ResearchReviewStep_NotesLabel {
  font-size: $fs-12;
  line-height: 18px;
  font-weight: fw('semi-bold');
  color: $oai-text-primary;
}

.oai-ResearchReviewStep_NotesFields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.oai-ResearchReviewStep_NoteField {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.oai-ResearchReviewStep_StructurePathItem {
  display: flex;
  align-items: center;
}
</style>
